import type { OAuthProvider, SignupRequest } from '@360dash/shared'

/**
 * auth-api — client for the existing dashboard-auth Cloudflare Worker.
 *
 * This is a separate client from api.ts because:
 * - It targets a different base URL (VITE_AUTH_API_URL)
 * - It does NOT auto-attach a JWT (these are pre-auth calls)
 * - Auth operations have distinct error shapes
 */

const getAuthBase = () => import.meta.env.VITE_AUTH_API_URL ?? ''

export class AuthApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string
  ) {
    super(message)
    this.name = 'AuthApiError'
  }
}

async function authRequest<T>(
  method: string,
  path: string,
  body?: unknown,
  bearerToken?: string
): Promise<T> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
  }

  let res: Response
  try {
    res = await fetch(`${getAuthBase()}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
  } catch {
    throw new AuthApiError(0, 'NETWORK_ERROR', 'Network error — check your connection')
  }

  const text = await res.text()
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    throw new AuthApiError(res.status, 'PARSE_ERROR', `Unexpected response from auth service`)
  }

  if (!res.ok) {
    const err = parsed as { error?: string; message?: string; code?: string }
    throw new AuthApiError(
      res.status,
      err?.code ?? 'AUTH_ERROR',
      err?.error ?? err?.message ?? `Auth failed (${res.status})`
    )
  }

  return parsed as T
}

/**
 * authApi — covers all interactions with the dashboard-auth Worker.
 *
 * All methods throw AuthApiError on failure.
 */
export const authApi = {
  /**
   * Password login. Turnstile token is optional (admin IP bypass).
   */
  login: (email: string, password: string, turnstileToken?: string) =>
    authRequest<{ token: string }>('POST', '/login', {
      email,
      password,
      ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
    }),

  signup: (payload: SignupRequest) =>
    authRequest<{ token?: string; message?: string }>('POST', '/signup', payload),

  /**
   * Complete password setup from an invite/reset token.
   */
  setupPassword: (setupToken: string, password: string) =>
    authRequest<{ message: string }>('POST', '/setup-password', {
      token: setupToken,
      password,
    }),

  /**
   * Change password (requires old password + current session).
   */
  changePassword: (oldPassword: string, newPassword: string, bearerToken: string) =>
    authRequest<{ message: string }>(
      'POST',
      '/change-password',
      { old_password: oldPassword, new_password: newPassword },
      bearerToken
    ),

  /**
   * Refresh JWT — returns new token if current token is still valid.
   */
  refreshToken: (currentToken: string) =>
    authRequest<{ token: string }>('POST', '/refresh', undefined, currentToken),

  getOAuthUrl: (provider: OAuthProvider, returnTo?: string) => {
    const url = new URL(`${getAuthBase()}/oauth/${provider}`)
    if (returnTo) url.searchParams.set('returnTo', returnTo)
    return url.toString()
  },
}
